import { memo } from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { AlarmClock, BellRing, FileText, Flag, Mail, MailOpen, Paperclip } from 'lucide-react';
import type { Mailbox, MailCategory, ThreadSummary } from '@mailpilot/shared';
import { Avatar } from '../ui/Avatar';
import { displayName, listTime } from '../../lib/format';
import { spring } from '../ui/motion';
import { CategoryChip } from './Category';

export type QuickAction = { id: string; flagged?: boolean; unread?: boolean };

function QuickButton({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      data-tip={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className="tip flex h-7 w-7 items-center justify-center rounded-lg text-muted transition-[background-color,transform] hover:scale-110 hover:bg-primary-soft hover:text-primary-ink active:scale-95"
    >
      {children}
    </button>
  );
}

export const ThreadRow = memo(function ThreadRow({
  thread,
  selected,
  mailbox,
  showAccount,
  onOpen,
  onQuick,
  tabIndex,
  index,
  animateIn,
  compact,
  category,
}: {
  thread: ThreadSummary;
  selected: boolean;
  mailbox: Mailbox | undefined;
  showAccount: boolean;
  onOpen: (id: string) => void;
  onQuick: (a: QuickAction) => void;
  tabIndex: number;
  index: number;
  animateIn: boolean;
  compact: boolean;
  category: MailCategory | undefined;
}) {
  const sender = thread.lastSender ?? thread.participants[0] ?? null;
  const name = displayName(sender);
  const accent = mailbox?.color ?? 'rgb(var(--primary))';
  const hasChips = (showAccount && mailbox) || category || thread.awaitingReplyFrom === 'me';
  return (
    <div className="group relative px-2 py-[2px]">
      <motion.button
        type="button"
        data-thread-id={thread.id}
        tabIndex={tabIndex}
        aria-current={selected ? 'true' : undefined}
        aria-label={`${thread.unread ? 'Unread. ' : ''}${name}: ${thread.subject || '(no subject)'}${mailbox && showAccount ? ` (${mailbox.displayName})` : ''}`}
        onClick={() => onOpen(thread.id)}
        initial={animateIn ? { opacity: 0, y: 8 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ ...spring, delay: animateIn ? Math.min(index, 12) * 0.03 : 0 }}
        whileTap={{ scale: 0.99 }}
        className={clsx(
          'relative flex w-full gap-3 overflow-hidden rounded-2xl text-left transition-[background-color,box-shadow] duration-150',
          compact ? 'px-3 py-2' : 'px-3 py-3',
          selected ? 'bg-primary-soft/80 shadow-[inset_0_0_0_1px_rgb(var(--primary)/0.25)]' : 'hover:bg-raised',
        )}
      >
        {/* unread / account accent bar */}
        <span aria-hidden className={clsx('absolute bottom-3 left-0 top-3 w-[3px] rounded-r-full transition-opacity', thread.unread || selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-50')} style={{ background: thread.unread ? accent : 'rgb(var(--primary) / 0.5)' }} />
        <span className="relative shrink-0">
          <Avatar name={sender?.name} email={sender?.email ?? thread.id} size={compact ? 32 : 38} />
          {thread.assignedTo && (
            <span className="absolute -bottom-1 -right-1" title={`Assigned to ${thread.assignedTo.name || thread.assignedTo.email}`}>
              <Avatar name={thread.assignedTo.name} email={thread.assignedTo.email} size={18} ring="rgb(var(--surface))" />
            </span>
          )}
        </span>
        <span className="relative min-w-0 flex-1">
          <span className="flex items-baseline gap-2">
            <span className={clsx('min-w-0 flex-1 truncate text-[0.8125rem] text-ink', thread.unread ? 'font-semibold' : 'font-medium')}>{name}</span>
            {/* the time makes room for quick actions on hover */}
            <span className={clsx('shrink-0 text-[0.6875rem] tabular-nums transition-opacity group-focus-within:opacity-0 group-hover:opacity-0', thread.unread ? 'font-semibold text-primary-ink' : 'text-subtle')}>{listTime(thread.lastMessageAt)}</span>
          </span>
          <span className={clsx('mt-0.5 flex items-center gap-1.5 text-[0.8125rem]', thread.unread ? 'font-semibold text-ink' : 'text-ink/90')}>
            <span className="truncate">{thread.subject || '(no subject)'}</span>
            {thread.messageCount > 1 && <span className="shrink-0 rounded-md bg-sunken px-1.5 text-[0.625rem] font-semibold tabular-nums text-muted">{thread.messageCount}</span>}
            <span className="ml-auto flex shrink-0 items-center gap-1 text-subtle">
              {thread.followUp?.due && (
                <span className="mp-pill mp-pill-salmon !h-[18px] !px-1.5 !text-[0.625rem]" title="You asked to be reminded if nobody replied">
                  <AlarmClock className="h-3 w-3" aria-hidden /> No reply
                </span>
              )}
              {thread.attentionPending && <BellRing className="h-3.5 w-3.5 text-danger" aria-label="Needs attention" />}
              {thread.hasPendingDraft && <FileText className="h-3.5 w-3.5" aria-label="Has draft" />}
              {thread.hasAttachments && <Paperclip className="h-3.5 w-3.5" aria-label="Has attachments" />}
              {thread.flagged && <Flag className="h-3.5 w-3.5 fill-warn text-warn" aria-label="Flagged" />}
            </span>
          </span>
          {!compact && <span className="mt-0.5 block truncate text-xs leading-relaxed text-muted">{thread.snippet}</span>}
          {hasChips && (
            <span className={clsx('flex flex-wrap items-center gap-1.5', compact ? 'mt-1' : 'mt-1.5')}>
              {showAccount && mailbox && (
                <span className="mp-pill mp-pill-neutral inline-flex max-w-[8rem] !h-[18px] truncate !px-1.5 !text-[0.625rem]">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: mailbox.color }} aria-hidden />
                  <span className="truncate">{mailbox.displayName}</span>
                </span>
              )}
              {category && <CategoryChip category={category} source={thread.categorySource} />}
              {thread.awaitingReplyFrom === 'me' && <span className="mp-pill mp-pill-yellow !h-[18px] !px-2 !text-[0.625rem]">Needs reply</span>}
            </span>
          )}
        </span>
      </motion.button>
      {/* Hover quick actions (mouse convenience; the same actions have keyboard shortcuts s / u). */}
      <div className={clsx('pointer-events-none absolute right-4 flex items-center gap-0.5 rounded-lg bg-surface/90 p-0.5 opacity-0 shadow-sm backdrop-blur transition-opacity duration-150 group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100', compact ? 'top-1.5' : 'top-2.5')}>
        <QuickButton label={thread.flagged ? 'Unflag' : 'Flag'} onClick={() => onQuick({ id: thread.id, flagged: !thread.flagged })}>
          <Flag className={clsx('h-3.5 w-3.5', thread.flagged && 'fill-current')} />
        </QuickButton>
        <QuickButton label={thread.unread ? 'Mark as read' : 'Mark as unread'} onClick={() => onQuick({ id: thread.id, unread: !thread.unread })}>
          {thread.unread ? <MailOpen className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
        </QuickButton>
      </div>
    </div>
  );
});
